// components/website/BillReceipt.js - Sugandhi Flowers Premium Bill Receipt
import React, { useRef } from 'react';
import { X, Printer, Flower, Heart, Clock, MapPin, Phone, Mail, CreditCard, Truck, CheckCircle, XCircle, Package } from 'lucide-react';

const BillReceipt = ({ order, onClose }) => {
  const receiptRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Status configuration
  const getStatusConfig = (status) => {
    const statusMap = {
      'delivered': { 
        label: 'DELIVERED', 
        color: '#10b981', 
        bg: '#d1fae5', 
        icon: CheckCircle,
        message: 'Order delivered successfully! Thank you for shopping.'
      },
      'cancelled': { 
        label: 'CANCELLED', 
        color: '#ef4444', 
        bg: '#fee2e2', 
        icon: XCircle,
        message: 'Order has been cancelled. Please contact support for any queries.'
      },
      'out': { 
        label: 'OUT FOR DELIVERY', 
        color: '#8b5cf6', 
        bg: '#ede9fe', 
        icon: Truck,
        message: 'Your order is out for delivery!'
      },
      'processing': { 
        label: 'PROCESSING', 
        color: '#f59e0b', 
        bg: '#fef3c7', 
        icon: Package,
        message: 'Your order is being prepared.'
      },
      'confirmed': { 
        label: 'CONFIRMED', 
        color: '#3b82f6', 
        bg: '#dbeafe', 
        icon: CheckCircle,
        message: 'Order confirmed. We will update you soon.'
      },
      'pending': { 
        label: 'PENDING', 
        color: '#64748b', 
        bg: '#f1f5f9', 
        icon: Clock,
        message: 'Order pending confirmation.'
      }
    };
    return statusMap[status] || statusMap.pending;
  };

  const handlePrint = () => {
    const printContent = receiptRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sugandhi Flowers - Order Receipt</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Inter', sans-serif; 
            background: #f8fafc; 
            padding: 20px;
            color: #1a0a0a;
          }
          .receipt-container {
            max-width: 500px;
            margin: 0 auto;
            background: white;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          }
          @media print {
            body { background: white; padding: 0; }
            .no-print { display: none; }
            .receipt-container { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          ${printContent}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const statusConfig = getStatusConfig(order.orderStatus);
  const StatusIcon = statusConfig.icon;

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0,0,0,0.6)', 
      zIndex: 4000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '24px', 
        width: '100%', 
        maxWidth: '520px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        animation: 'slideUp 0.3s ease'
      }}>
        {/* Header with Sugandhi Branding */}
        <div style={{ 
          padding: '20px 24px', 
          background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: 'white'
        }}>
          <div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0, color:"#fff",display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Flower size={22} /> Sugandhi Flowers
            </h2>
            <p style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '4px' }}>Premium Florist • Solapur</p>
          </div>
          <button 
            onClick={onClose} 
            className="no-print"
            style={{ 
              background: 'rgba(255,255,255,0.2)', 
              border: 'none', 
              width: '36px', 
              height: '36px', 
              borderRadius: '50%', 
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <X size={18} />
          </button>
        </div>

        {/* Receipt Content */}
        <div ref={receiptRef} style={{ padding: '24px' }}>
          
          {/* Order Status Banner - BIG & CLEAR */}
          <div style={{ 
            background: statusConfig.bg, 
            borderRadius: '16px', 
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'center',
            border: `2px solid ${statusConfig.color}`,
            boxShadow: `0 2px 8px ${statusConfig.color}20`
          }}>
            <StatusIcon size={32} color={statusConfig.color} style={{ marginBottom: '8px' }} />
            <div style={{ 
              fontSize: '1.3rem', 
              fontWeight: 800, 
              color: statusConfig.color,
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              {statusConfig.label}
            </div>
            <p style={{ fontSize: '0.7rem', color: '#475569', marginTop: '4px' }}>{statusConfig.message}</p>
          </div>

          {/* Shop Logo & Info */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{
            width: '210px',
            height: '150px',
            background: 'white',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            padding: '5px'
          }}>
            <img 
              src="/Sugandhi Logo04.png" 
              alt="Sugandhi Flowers" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="white"/%3E%3Ctext x="50" y="67" text-anchor="middle" fill="%23dc2626" font-size="50" font-weight="bold"%3ES%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
            <p style={{ fontSize: '0.6rem', color: '#64748b' }}>Fresh Flowers • Same Day Delivery • Solapur</p>
          </div>

          {/* Order Info Card */}
          <div style={{ 
            background: '#fefaf8', 
            borderRadius: '12px', 
            padding: '14px', 
            marginBottom: '20px',
            border: '1px solid rgba(220,38,38,0.1)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <p style={{ fontSize: '0.6rem', color: '#64748b', marginBottom: '2px' }}>ORDER ID</p>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace', color: '#1a0a0a' }}>#{order.id?.slice(0, 12)}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.6rem', color: '#64748b', marginBottom: '2px' }}>TRANSACTION ID</p>
                <p style={{ fontSize: '0.7rem', fontWeight: 500, fontFamily: 'monospace', color: '#1a0a0a' }}>{order.transactionId?.slice(0, 12)}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.6rem', color: '#64748b', marginBottom: '2px' }}>ORDER DATE</p>
                <p style={{ fontSize: '0.7rem', fontWeight: 500 }}>{formatDate(order.orderDate)}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.6rem', color: '#64748b', marginBottom: '2px' }}>PAYMENT</p>
                <p style={{ 
                  fontSize: '0.65rem', 
                  fontWeight: 600,
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  background: order.paymentMethod === 'Cash on Delivery' ? '#fef3c7' : '#d1fae5',
                  color: order.paymentMethod === 'Cash on Delivery' ? '#92400e' : '#065f46'
                }}>
                  {order.paymentMethod === 'Cash on Delivery' ? 'Cash On Delivery' : 'Online'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: '8px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Heart size={12} /> Customer Details
            </h3>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>{order.customerName?.[0]?.toUpperCase()}</span>
                </div>
                <div>
                  <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a0a0a' }}>{order.customerName}</p>
                  <p style={{ fontSize: '0.6rem', color: '#64748b' }}>{order.customerEmail || 'No email provided'}</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: '6px', borderTop: '1px solid #e2e8f0' }}>
                <Phone size={11} color="#dc2626" />
                <span style={{ fontSize: '0.7rem', color: '#1a0a0a' }}>{order.customerPhone}</span>
              </div>
            </div>
          </div>

          {/* Delivery Address */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: '8px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MapPin size={12} /> Delivery Address
            </h3>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px' }}>
              <p style={{ fontSize: '0.75rem', lineHeight: 1.4, color: '#1a0a0a' }}>{order.deliveryAddress?.line1}</p>
              {order.deliveryAddress?.landmark && (
                <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '4px' }}>Landmark: {order.deliveryAddress?.landmark}</p>
              )}
              <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '4px' }}>
                {order.deliveryAddress?.city} - {order.deliveryAddress?.pincode}
              </p>
            </div>
          </div>

          {/* Order Items */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: '8px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CreditCard size={12} /> Order Items
            </h3>
            <div style={{ background: '#f8fafc', borderRadius: '12px', overflow: 'hidden', padding: '8px' }}>
              {order.items?.map((item, idx) => (
                <div key={idx} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px',
                  borderBottom: idx < order.items.length - 1 ? '1px solid #e2e8f0' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }}
                      onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=60&q=80'}
                    />
                    <div>
                      <p style={{ fontSize: '0.7rem', fontWeight: 600, color: '#1a0a0a' }}>{item.name}</p>
                      <p style={{ fontSize: '0.6rem', color: '#64748b' }}>Qty: {item.quantity}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc2626' }}>
                    {formatCurrency(item.price * item.quantity)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Price Summary */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ background: '#fff5f5', borderRadius: '12px', padding: '14px', border: '1px solid rgba(220,38,38,0.15)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Subtotal</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{formatCurrency(order.totalAmount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Delivery Charges</span>
                <span style={{ fontSize: '0.7rem', color: '#10b981', fontWeight: 500 }}>N/A</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Packaging</span>
                <span style={{ fontSize: '0.7rem', color: '#10b981', fontWeight: 500 }}>N/A</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginTop: '10px', 
                paddingTop: '10px', 
                borderTop: '1px dashed #dc2626'
              }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a0a0a' }}>Total Amount</span>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#dc2626' }}>{formatCurrency(order.totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Delivery Time (if available) */}
          {order.deliveryTime && (
            <div style={{ 
              background: '#fef3c7', 
              borderRadius: '10px', 
              padding: '10px', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Clock size={14} color="#f59e0b" />
              <div>
                <p style={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 500 }}> Delivery Time Allocated</p>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a0a0a' }}>{order.deliveryTime}</p>
              </div>
            </div>
          )}

          {/* Thank You Message */}
          <div style={{ textAlign: 'center', marginTop: '16px', padding: '14px', background: '#fff5f5', borderRadius: '12px' }}>
            <Heart size={16} color="#dc2626" style={{ marginBottom: '6px' }} />
            <p style={{ fontSize: '0.7rem', fontWeight: 500, color: '#dc2626', marginBottom: '2px' }}>
              Thank you for choosing Sugandhi Flowers!
            </p>
            <p style={{ fontSize: '0.55rem', color: '#64748b' }}>
              Freshness delivered with love. 🌸
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e2e8f0', 
          display: 'flex', 
          gap: '12px',
          background: '#f8fafc'
        }} className="no-print">
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '10px',
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: 800,
              cursor: 'pointer',
              color: '#fff',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
          >
            Close
          </button>
       {/*   <button
            onClick={handlePrint}
            style={{
              flex: 1,
              padding: '10px',
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Printer size={14} /> Print Receipt
          </button>*/}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default BillReceipt;